import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type { PagedPlanes, Plan, PlanCreateInput, PlanUpdateInput, TipoServicio } from "./schemas";

const KEY = "planes";

export type PlanesFilters = {
  page?: number;
  size?: number;
  search?: string;
  tipo_servicio?: TipoServicio | "";
  activo?: boolean | null;
};

function buildParams(f: PlanesFilters): Record<string, string | number> {
  const p: Record<string, string | number> = { page: f.page ?? 1, size: f.size ?? 20 };
  if (f.search) p.search = f.search;
  if (f.tipo_servicio) p.tipo_servicio = f.tipo_servicio;
  if (f.activo !== null && f.activo !== undefined) p.activo = f.activo ? "true" : "false";
  return p;
}

export function useListPlanes(filters: PlanesFilters) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () =>
      apiCall<PagedPlanes>("get", "/api/v1/planes", { searchParams: buildParams(filters) }),
    placeholderData: (prev) => prev,
  });
}

export function usePlan(id: string | null) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => apiCall<Plan>("get", `/api/v1/planes/${id}`),
    enabled: !!id,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanCreateInput) => apiCall<Plan>("post", "/api/v1/planes", { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useUpdatePlan(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PlanUpdateInput) =>
      apiCall<Plan>("patch", `/api/v1/planes/${id}`, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiCall<{ message: string }>("delete", `/api/v1/planes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
